import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@radix-ui/react-dropdown-menu";
import { CaretDown, CheckSquare, Square } from "@phosphor-icons/react";
import { UserRole } from "@common/utils/graphql";
import { cn } from "@/utils/utils";
import { Fragment } from "react";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type UserRoleOption = {
  role: UserRole;
  disabled?: boolean;
};
type Props = {
  userRoles: UserRole[];
  onChange: (roles: UserRole[]) => void;
  options: UserRoleOption[];
};

export const SelectUserRoles = ({ userRoles, onChange, options }: Props) => {
  const onSelect = (e: Event, roleOption: UserRoleOption) => {
    e.preventDefault();
    const filtered = userRoles.filter((v) => v !== roleOption.role);
    if (filtered.length === userRoles.length) {
      onChange([...userRoles, roleOption.role]);
    } else {
      onChange(filtered);
    }
  };

  const showPlaceholder = userRoles.length === 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-[#1c1c1c] px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1">
        <span
          className={cn(
            "text-sm capitalize",
            showPlaceholder && "text-muted-foreground",
          )}
        >
          {showPlaceholder ? "Select roles" : userRoles.join(", ")}
        </span>
        <CaretDown className="size-4 opacity-50" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] px-3 py-3">
        {options.map((roleOption, index) => {
          const { role, disabled } = roleOption;
          const selected = userRoles.includes(role);
          return (
            <Fragment key={`role-checkbox--fragment-${role}`}>
              {index !== 0 && (
                <Separator className="mx-3 h-[1px] bg-white/[0.1]" />
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <DropdownMenuItem
                      key={`role-checkbox--${role}`}
                      onSelect={(e) => onSelect(e, roleOption)}
                      disabled={disabled}
                      className="flex w-full items-center justify-between p-3 capitalize"
                    >
                      {role}
                      {selected ? (
                        <CheckSquare weight="fill" className="size-6" />
                      ) : (
                        <Square className="size-6 fill-white/30" />
                      )}
                    </DropdownMenuItem>
                  </div>
                </TooltipTrigger>
                {role === UserRole.Admin && disabled && (
                  <TooltipContent>
                    <p className="text-sm">{"Only editable in Admin View"}</p>
                    <TooltipArrow />
                  </TooltipContent>
                )}
              </Tooltip>
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
